import styled, { keyframes } from "styled-components";
import { defaultTheme } from "_constants";
import AppLottie from "_lib/AppLottie";
import PropTypes from "prop-types";
import { useIsMobile } from "_hooks";

const R = 20;
const CX = 24;
const CIRCUMFERENCE = 2 * Math.PI * R;   // ~125.66
const ARC_LENGTH = CIRCUMFERENCE * 0.3;   // 30% arc — clearly visible running motion

const runAround = keyframes`
  0%   { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: ${-CIRCUMFERENCE}; }
`;

const StyledAppLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${({ height }) => height};
  position: ${({ position }) => position};
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    height: ${({ height }) => height};
  }
`;

const AnimatedArc = styled.circle`
  stroke-dasharray: ${ARC_LENGTH} ${CIRCUMFERENCE};
  stroke-dashoffset: 0;
  animation: ${runAround} 0.9s linear infinite;
`;

const ArcSpinner = ({ size = 48, color, trackColor = "#e2e5ed", strokeWidth = 4 }) => {
  const scale = size / 48;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      style={{ display: "block" }}
    >
      {/* Track */}
      <circle
        cx={CX}
        cy={CX}
        r={R}
        stroke={trackColor}
        strokeWidth={strokeWidth / scale}
      />
      {/* Running arc — starts at 12 o'clock, travels clockwise */}
      <AnimatedArc
        cx={CX}
        cy={CX}
        r={R}
        stroke={color}
        strokeWidth={strokeWidth / scale}
        strokeLinecap="round"
        transform={`rotate(-90 ${CX} ${CX})`}
      />
    </svg>
  );
};

const AppLoader = (props) => {
  const {
    lottie = false,
    position = "absolute",
    height = "100%",
    lottieAnimationData,
    lottieClassName,
    lottieSize = 300,
    size = 48,
  } = props;
  const isMobile = useIsMobile();
  return (
    <StyledAppLoader position={position} height={height}>
      {lottie ? (
        <AppLottie
          className={lottieClassName}
          animationData={lottieAnimationData}
          size={isMobile ? lottieSize / 2 : lottieSize}
          loop={true}
        />
      ) : (
        <ArcSpinner
          size={size}
          color={defaultTheme.theme.palette.primary.main}
        />
      )}
    </StyledAppLoader>
  );
};

AppLoader.propTypes = {
  lottie: PropTypes.bool,
  position: PropTypes.string,
  height: PropTypes.string,
  lottieAnimationData: PropTypes.object,
  lottieClassName: PropTypes.string,
  lottieSize: PropTypes.number,
  size: PropTypes.number,
};

export default AppLoader;
